import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, LogOut } from 'lucide-react';
import { House } from '../../data/houses';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { houseKioskRoutes } from '../../routes';
import AnimatedBackground from './AnimatedBackground';
import HouseParticles from './HouseParticles';
import ScannerCard from './ScannerCard';
import HouseSelector from './HouseSelector';
import Screensaver from '../Shared/Screensaver';

const IDLE_TIMEOUT = 60000; // 60 seconds

export default function KioskView() {
  const { activeHouses, addPoints, getHouse, getMascotUserId, updateHouse, forceRefresh } = useApp();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [selectedHouse, setSelectedHouse] = useState<House | null>(null);
  const [showScreensaver, setShowScreensaver] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Force refresh standings when navigating to this page
  useEffect(() => {
    forceRefresh();
  }, [forceRefresh]);

  // Set initial house when activeHouses changes
  useEffect(() => {
    if (activeHouses.length > 0 && !selectedHouse) {
      setSelectedHouse(activeHouses[0]);
    }
    // Update selected house if it was archived
    if (selectedHouse && !activeHouses.find(h => h.id === selectedHouse.id)) {
      setSelectedHouse(activeHouses[0] || null);
    }
  }, [activeHouses, selectedHouse]);

  // Reset activity timer on any interaction
  const resetActivityTimer = useCallback(() => {
    setLastActivity(Date.now());
    if (showScreensaver) {
      setShowScreensaver(false);
    }
  }, [showScreensaver]);

  // Check for idle timeout
  useEffect(() => {
    const checkIdle = () => {
      if (Date.now() - lastActivity > IDLE_TIMEOUT && !showScreensaver) {
        setShowScreensaver(true);
      }
    };

    const interval = setInterval(checkIdle, 1000);
    return () => clearInterval(interval);
  }, [lastActivity, showScreensaver]);

  // Add activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, resetActivityTimer);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, resetActivityTimer);
      });
    };
  }, [resetActivityTimer]);

  const handleScan = () => {
    resetActivityTimer();
  };

  const handleSelectHouse = (house: House) => {
    setSelectedHouse(house);
    resetActivityTimer();
  };

  // const handleBack = () => {
  //   if (location.state?.from) {
  //     navigate(location.state.from);
  //   } else if (currentUser?.type === 'teacher') {
  //     navigate(houseKioskRoutes.teacher);
  //   } else if (currentUser?.type === 'mascot') {
  //     navigate(houseKioskRoutes.mascot(currentUser.houseId));
  //   } else {
  //     navigate(houseKioskRoutes.login);
  //   }
  // };

  const handleLogout = () => {
    logout();
    navigate(houseKioskRoutes.login);
  };

  const handleViewStandings = () => {
    navigate(houseKioskRoutes.leaderboard);
  };

  // Get current house data from context
  const currentHouse = selectedHouse ? getHouse(selectedHouse.id) : null;

  if (!currentHouse || activeHouses.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">No active houses configured</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden font-sans">
      <AnimatedBackground houseId={currentHouse.id} />
      <HouseParticles houseId={currentHouse.id} />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-between p-8 py-12">
        {/* Navigation Buttons */}
        <div className="absolute top-4 left-4 flex gap-3">
          {/* <button
            onClick={handleBack}
            className="backdrop-blur-lg bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20"
          >
            <ArrowLeft size={20} />
          </button> */}
          <button
            onClick={handleLogout}
            className="backdrop-blur-lg bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-300 border border-white/20"
          >
            <LogOut size={20} />
          </button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center w-full space-y-8">
          <ScannerCard 
            house={currentHouse} 
            onScan={handleScan} 
            addPoints={addPoints}
            updateHouse={updateHouse}
            mascotUserId={getMascotUserId(currentHouse.id)}
          />
        </div>

        <div className="space-y-6 w-full flex flex-col items-center mt-4">
          <HouseSelector
            houses={activeHouses}
            selectedHouse={currentHouse}
            onSelectHouse={handleSelectHouse}
          />

          <button
            onClick={handleViewStandings}
            className="backdrop-blur-lg bg-white/20 hover:bg-white/30 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 border-2 border-white/30 hover:border-white/50 shadow-xl hover:shadow-2xl hover:scale-105 flex items-center space-x-3"
          >
            <Trophy size={24} />
            <span className="text-lg">VIEW STANDINGS</span>
          </button>
        </div>
      </div>

      {showScreensaver && (
        <Screensaver
          houses={activeHouses}
          onDismiss={resetActivityTimer}
        />
      )}
    </div>
  );
}
